/**
 * Date comparison utilities
 * Provides dayjs equivalents for date-fns comparison functions
 */
import { type Dayjs } from './dayjs-config';
import { toDayjs } from './parse-iso-safe';

/**
 * Check if a date is today
 * Equivalent to date-fns's isToday
 *
 * @param date - Date to check
 * @returns True if the date is today
 */
export function isToday(date: Date | Dayjs | string | null | undefined): boolean {
  const dayjsDate = toDayjs(date);
  if (!dayjsDate) return false;

  return dayjsDate.isToday();
}

/**
 * Check if a date is tomorrow
 * Equivalent to date-fns's isTomorrow
 *
 * @param date - Date to check
 * @returns True if the date is tomorrow
 */
export function isTomorrow(date: Date | Dayjs | string | null | undefined): boolean {
  const dayjsDate = toDayjs(date);
  if (!dayjsDate) return false;

  return dayjsDate.isTomorrow();
}

/**
 * Check if two dates are on the same day
 * Equivalent to date-fns's isSameDay
 *
 * @param date1 - First date
 * @param date2 - Second date
 * @returns True if both dates are on the same day
 */
export function isSameDay(
  date1: Date | Dayjs | string | null | undefined,
  date2: Date | Dayjs | string | null | undefined,
): boolean {
  const dayjs1 = toDayjs(date1);
  const dayjs2 = toDayjs(date2);

  if (!dayjs1 || !dayjs2) return false;

  return dayjs1.isSame(dayjs2, 'day');
}

/**
 * Check if a date is the same or before another date
 *
 * @param date - Date to check
 * @param dateToCompare - Date to compare against
 * @returns True if date is same or before dateToCompare
 */
export function isSameOrBefore(
  date: Date | Dayjs | string | null | undefined,
  dateToCompare: Date | Dayjs | string | null | undefined,
): boolean {
  const dayjs1 = toDayjs(date);
  const dayjs2 = toDayjs(dateToCompare);

  if (!dayjs1 || !dayjs2) return false;

  return dayjs1.isSameOrBefore(dayjs2);
}

/**
 * Check if a date is the same or after another date
 *
 * @param date - Date to check
 * @param dateToCompare - Date to compare against
 * @returns True if date is same or after dateToCompare
 */
export function isSameOrAfter(
  date: Date | Dayjs | string | null | undefined,
  dateToCompare: Date | Dayjs | string | null | undefined,
): boolean {
  const dayjs1 = toDayjs(date);
  const dayjs2 = toDayjs(dateToCompare);

  if (!dayjs1 || !dayjs2) return false;

  return dayjs1.isSameOrAfter(dayjs2);
}

/**
 * Check if two dates are in the same minute (ignoring seconds and milliseconds)
 *
 * @param date1 - First date
 * @param date2 - Second date
 * @returns True if both dates are in the same minute
 *
 * @example
 * isSameMinute('2024-01-15T10:30:00', '2024-01-15T10:30:45') // true
 * isSameMinute('2024-01-15T10:30:00', '2024-01-15T10:31:00') // false
 */
export function isSameMinute(
  date1: Date | Dayjs | string | null | undefined,
  date2: Date | Dayjs | string | null | undefined,
): boolean {
  const dayjs1 = toDayjs(date1);
  const dayjs2 = toDayjs(date2);

  if (!dayjs1 || !dayjs2) return false;

  return dayjs1.isSame(dayjs2, 'minute');
}
